// =============================================================================
// SIREPPAR - Modal de informe genérico (texto libre editable)
// =============================================================================
// Reutilizable para los informes que son solo cuerpo redactado (sin checklist de
// atributos), como los de la etapa de documentación:
//   - "Informe al Secretario (Documentación)" → elevar a EN_SECRETARIO
//   - "Informe al TEP (Documentación)"        → elevar a EN_TEP
// y los del circuito "dado por NO PRESENTADO" (informe al Secretario y del
// Secretario al TEP), que agregan dos opciones:
//   - conHistorial:    panel al costado con las intimaciones/observaciones y sus
//     respuestas; al tildarlas se arma el bloque "Antecedentes" al final del
//     cuerpo, siempre de la más antigua a la más reciente (mismo mecanismo de
//     bloque gestionado que usa ModalInformeSecretario con los atributos objetados,
//     así lo que redacta arriba el usuario nunca se pisa).
//   - conAvisoPartido: check al pie para decidir si se le manda copia al partido.
// Botones:
//   - "Generar borrador": genera y descarga el PDF sin persistir (regenerable).
//   - "Guardar informe":  genera, descarga, guarda el informe como Documento y
//     transiciona la fase (lo hace el endpoint con persistir=true).
// =============================================================================
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import RichTextEditor from './RichTextEditor';
import { api } from '../../services/api';
import PanelHistorialInforme from './PanelHistorialInforme';
import { aplicarAntecedentes, ItemHistorialInforme } from '../../utils/historialInforme';
import AdjuntosInforme from './AdjuntosInforme';

export interface InformeSimpleConfig {
  endpoint: string;        // p. ej. 'elevar-secretario-documentacion'
  titulo: string;
  textoInicial: string;
  confirmMsg: string;      // window.confirm antes de guardar/persistir
  successMsg: string;
  ayuda?: string;          // texto de ayuda arriba del editor
  // Prefijo de la API. Por defecto 'reconocimiento' (uso histórico del modal);
  // el circuito de no presentado pasa 'no-presentado'.
  basePath?: string;
  // Panel al costado con el historial de intimaciones/observaciones + respuestas.
  conHistorial?: boolean;
  // Check al pie: se manda al endpoint como notificarPartido.
  conAvisoPartido?: boolean;
  // Aclaración debajo del check. Los informes de la vía SEP → Secretario → TEP son
  // internos: al partido se le avisa que el expediente avanzó, pero NO se le manda
  // el informe. Otros usos del modal (caducidad, nuevo plazo) sí adjuntan el acta.
  subtextoAvisoPartido?: string;
  labelAvisoPartido?: string;
  // Deja el check de aviso TILDADO al abrir. Es para los informes que el partido
  // tiene que recibir siempre (el inicio del trámite es su acuse de apertura, y la
  // elevación al Secretario lleva la intimación de los Arts. 17/18/19): se puede
  // destildar, pero el default es notificar.
  avisoPartidoPorDefecto?: boolean;
  // Etiqueta del botón que persiste. Por defecto "Guardar informe".
  labelGuardar?: string;
  // Bloque para subir respaldo (captura, correo, nota escaneada). Cuando está en true
  // el request pasa de JSON a multipart; los circuitos que no lo piden (no-presentado,
  // reempadronamiento) siguen mandando JSON y no hay que tocar sus endpoints.
  conAdjuntos?: boolean;
  ayudaAdjuntos?: string;
  // Campos extra que viajan al endpoint junto con el texto. Sirve para las variantes
  // de un mismo informe que cambian el efecto y no el contenido: la alianza dada por
  // NO PRESENTADA usa este mismo modal y sólo agrega `noPresentado: true`.
  extraBody?: Record<string, string | number | boolean>;
}

interface Props {
  config: InformeSimpleConfig | null;
  expedienteId: string;
  onClose: () => void;
  onGuardado?: () => void;
}

export default function ModalInformeSimple({ config, expedienteId, onClose, onGuardado }: Props) {
  const [texto, setTexto] = useState('');
  const [generando, setGenerando] = useState(false);
  const [avisarPartido, setAvisarPartido] = useState(false);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const basePath = config?.basePath || 'reconocimiento';

  // Al abrir con una config nueva, precargar su texto (el panel del historial
  // limpia su selección solo, con resetKey={config}).
  useEffect(() => {
    if (config) {
      setTexto(config.textoInicial);
      setAvisarPartido(!!config.avisoPartidoPorDefecto);
      setAdjuntos([]);
    }
  }, [config]);

  // Al tildar/destildar en el panel se reconstruye solo el bloque "Antecedentes"
  // del final; lo que el usuario redactó arriba queda intacto.
  const aplicarSeleccion = (items: ItemHistorialInforme[]) => {
    setTexto((cuerpo) => aplicarAntecedentes(items, cuerpo));
  };

  const descargarPdf = (pdfBase64: string, filename: string) => {
    const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'informe.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generar = async (persistir: boolean) => {
    if (!config) return;
    if (!texto.trim()) {
      toast.error('Redactá el cuerpo del informe');
      return;
    }
    if (persistir && !window.confirm(config.confirmMsg)) return;
    setGenerando(true);
    try {
      // Con adjuntos el endpoint es multipart; sin adjuntos se manda JSON, para no
      // tener que migrar los circuitos que no los usan (no-presentado, reempadronamiento).
      const url = `/${basePath}/${expedienteId}/${config.endpoint}`;
      let res;
      if (config.conAdjuntos) {
        const fd = new FormData();
        fd.append('texto', texto.trim());
        fd.append('persistir', String(persistir));
        if (config.conAvisoPartido) fd.append('notificarPartido', String(avisarPartido));
        Object.entries(config.extraBody || {}).forEach(([k, v]) => fd.append(k, String(v)));
        adjuntos.forEach((f) => fd.append('adjuntos', f));
        res = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        res = await api.post(url, {
          texto: texto.trim(),
          persistir,
          ...(config.conAvisoPartido ? { notificarPartido: avisarPartido } : {}),
          ...(config.extraBody || {}),
        });
      }
      descargarPdf(res.data.data.pdfBase64, res.data.data.filename);
      toast.success(persistir ? config.successMsg : 'Borrador generado');
      if (persistir) {
        onGuardado?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al generar el informe');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Modal
      isOpen={config !== null}
      onClose={onClose}
      title={config?.titulo ?? 'Informe'}
      size={config?.conHistorial ? 'full' : 'xl'}
    >
      <div className="space-y-5">
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          {config?.ayuda ?? 'Redactá el informe. Podés dar formato al texto (negrita, cursiva, listas).'}
        </p>

        <div className={config?.conHistorial ? 'grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4' : ''}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cuerpo del informe</label>
            <RichTextEditor
              value={texto}
              onChange={(v) => setTexto(v ?? '')}
              minHeight={280}
              placeholder="Redacte el informe…"
            />
          </div>

          {config?.conHistorial && (
            <PanelHistorialInforme
              expedienteId={expedienteId}
              resetKey={config}
              onSeleccion={aplicarSeleccion}
            />
          )}
        </div>

        {config?.conAdjuntos && (
          <AdjuntosInforme archivos={adjuntos} onChange={setAdjuntos} ayuda={config.ayudaAdjuntos} />
        )}

        {config?.conAvisoPartido && (
          <label className="flex items-start gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-lg p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4"
              checked={avisarPartido}
              onChange={(e) => setAvisarPartido(e.target.checked)}
            />
            <span>
              {config.labelAvisoPartido || 'Avisar al partido por correo'}
              <span className="block text-xs text-gray-500">
                {config.subtextoAvisoPartido
                  || 'Si no lo tildás, no se le notifica nada al partido.'}
              </span>
            </span>
          </label>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => generar(false)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Generando…' : '📄 Generar borrador'}
          </button>
          <button
            type="button"
            onClick={() => generar(true)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Procesando…' : `✅ ${config?.labelGuardar || 'Guardar informe'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
